import {getHandlerByName} from "./handlers";
import Processing from "./Processing";
import * as api from "../utils/api";
import Creative from "./Creative";
import {throttle} from "../utils/etc";
import analyticsHelper from "../helpers/analytics.helper";

const localStorageKey = "npp:processing";

export class ProcessingManager {

  _listeners = [];
  _processing = null;
  _handlers = [];

  /** @return {Processing} */
  get processing() {
    return this._processing;
  }

  /** @param {Processing} processing */
  start = (processing) => {
    this.stop();

    this._processing = processing;

    if (!processing.hasExtra(Processing.EXTRA_STARTED_AT)) {
      processing.setExtra(Processing.EXTRA_STARTED_AT, Date.now());
    }

    window.localStorage.setItem(localStorageKey, processing.toJSON());

    this.update();
  };

  update = () => {
    if (!this.processing) {
      return;
    }

    const processing = this.processing;

    processing.creatives.forEach((creative) => {
      if (creative.getExtra(Creative.EXTRA_KEEP_PENDING, false) === true) {
        return;
      }

      if (creative.isFinished) {
        return;
      }

      const isActive = this._handlers.findIndex((item) => item.id === creative.id) > -1;
      if (isActive) {
        return;
      }

      const handler = getHandlerByName(creative.handler);
      if (handler === null) {
        throw new Error("Unrecognized handler name: " + creative.handler);
      }

      if (!creative.hasExtra(Creative.EXTRA_STARTED_AT)) {
        creative.setExtra(Creative.EXTRA_STARTED_AT, Date.now());
        analyticsHelper.generationStart(processing, creative);
      }

      const handlerPromise = handler(processing, creative, {})
        .then(() => {
          this.tick();
          analyticsHelper.generationFinish(processing, creative);
        })
        .catch((error) => {
          console.error(error);

          this.tick();
          analyticsHelper.generationError(processing, creative);
        });

      this._handlers.push({
        id: creative.id,
        handler: handlerPromise,
      });
    });

    this.tick();
  };

  /** @return {Processing} */
  restore = () => {
    const storedValue = window.localStorage.getItem(localStorageKey);
    if (storedValue != null) {
      const processing = new Processing();
      processing.fromObject(JSON.parse(storedValue));

      return processing;
    }

    return null;
  };

  stop = () => {
    this._processing = null;
  };

  clear = () => {
    window.localStorage.removeItem(localStorageKey);
    this.stop();
  };

  tickImmediately = () => {
    if (!this.processing) {
      return;
    }

    this.commit(this.processing.toJSON());

    this._listeners.forEach((listener) => {
      listener.call(null, this.processing);
    });
  };

  tick = throttle(200, this.tickImmediately);

  commit = (data, commitToApi = false) => {
    window.localStorage.setItem(localStorageKey, data);

    if (commitToApi) {
      this.commitToApi(data);
    }
  };

  commitProcessing = () => {
    this.processing && this.commit(this.processing.toJSON());
  };

  commitToApi = (data) => {
    clearTimeout(this.commitTimer);
    this.commitTimer = setTimeout(() => {
      api.processingCommit(data).then(() => {
        console.info("Processing committed");
      }).catch((err) => {
        console.error("Processing commit is failed", err);
      });
    }, 2000);
  };

  addOnProcessingChangeHandler = (listener) => {
    this._listeners.push(listener);
  };

  removeOnProcessingChangeHandler = (listener) => {
    const pos = this._listeners.indexOf(listener);
    if (pos >= 0) {
      this._listeners.splice(pos, 1);
    }
  };
}

export default new ProcessingManager();